// Calls to /api/signoff-admin, the parent-only endpoint behind the
// "Daily sign-off email" card. The access token is read from the current
// session on every call: never cached, never stored, never put in a URL,
// and only ever sent to this same-origin endpoint.
import { supabase } from './supabase';

const ENDPOINT = '/api/signoff-admin';

// kind: 'forbidden' (401/403: not a parent of this student, or signed out)
//       'failed'    (network error, 5xx, or an unreadable answer)
export class SignoffAdminError extends Error {
  constructor(kind, status) {
    super(`signoff-admin ${kind} (${status})`);
    this.kind = kind;
    this.status = status;
  }
}

async function accessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

async function call(method, { query = '', body } = {}) {
  const token = await accessToken();
  if (!token) throw new SignoffAdminError('forbidden', 401);
  let response;
  try {
    response = await fetch(ENDPOINT + query, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store',
      credentials: 'same-origin',
    });
  } catch {
    throw new SignoffAdminError('failed', 0);
  }
  if (response.status === 401 || response.status === 403) {
    throw new SignoffAdminError('forbidden', response.status);
  }
  if (!response.ok) throw new SignoffAdminError('failed', response.status);
  try {
    return await response.json();
  } catch {
    throw new SignoffAdminError('failed', response.status);
  }
}

export function fetchSignoffStatus(studentId) {
  return call('GET', { query: `?student_id=${encodeURIComponent(studentId)}` });
}

export function postSignoffAction(studentId, action) {
  return call('POST', { body: { student_id: studentId, action } });
}
